// Axios request guard for location mix-ups.
// - Blocks every write from a tab whose location was switched in another tab and
//   that has not reloaded yet (the "Location changed" dialog is showing).
// - Warns in the console, without blocking, when a write targets a location other
//   than the one this tab rendered.
import { getRenderedGroupId, getActiveGroupId, state } from '@/util/activeGroup'

export function extractGroupId(url: string): number | null {
  const m = url.match(/\/groups\/(\d+)(?:\/|\?|$)/)
  if (m) return Number(m[1])
  const q = url.match(/[?&](?:company_group_id|group)=(\d+)/)
  return q ? Number(q[1]) : null
}

export type GuardDecision = 'pass' | 'warn' | 'block'

export function decide(
  method: string,
  url: string,
  allowCrossGroup: boolean,
  rendered: number | null,
  stored: number | null,
  externalChangePending: boolean,
): GuardDecision {
  const m = (method || 'get').toLowerCase()
  if (m === 'get' || m === 'head' || m === 'options') return 'pass'
  // allowCrossGroup marks a location switch, which reloads the page itself, so it may run
  // even while the "Location changed" dialog is up
  if (allowCrossGroup) return 'pass'
  if (externalChangePending) return 'block'
  const target = extractGroupId(url)
  if (target === null || rendered === null) return 'pass'
  if (target !== rendered || (stored !== null && stored !== rendered)) return 'warn'
  return 'pass'
}

export function installGuard(axios: any): void {
  axios.interceptors.request.use((config: any) => {
    const url = config.url || ''
    const d = decide(config.method, url, !!config.allowCrossGroup, getRenderedGroupId(), getActiveGroupId(), !!state.externalChange)
    if (d === 'warn') {
      console.warn('[groupGuard] write targets location ' + extractGroupId(url) + ' but this tab rendered ' + getRenderedGroupId() + ': ' + url)
    }
    if (d === 'block') return Promise.reject(new Error('This tab is out of date. Reload to continue.'))
    return config
  })
}
